import React, { useMemo } from 'react'
import { ICellRendererParams } from 'ag-grid-enterprise'
import { Button } from 'antd'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { EnhancedStatementRow } from '../../../../utils/dataEnhancement'
import { notLocked } from '../../../../../../pages/settings/company/budgeting/budgetingScenarios/utils'
import { filtersSelector } from '../../../../../../redux/context/filters/selectors'
import { useCtxCompanyGroupBudgetSource } from '../../../../../companyGroup/queries/companyGroupSetting/useCtxCompanyGroupSettings'
import { GroupSettingDataSource } from '../../../../../companyGroup/types/CompanyGroupSettings'
import Authorized from '../../../../../../components/Authorized/Authorized'
import { ContractProduct } from '../../../../../../types/contract/Contract'
import { useModalActions } from '../../ModalContext'
import { useDeprecationPlanContext } from '../../../../../deprecationPlan/context/DeprecationPlanContext'

export const InvestmentMenuCellRenderer: React.FC<ICellRendererParams<EnhancedStatementRow>> = ({ data }) => {
  const { t } = useTranslation()
  const { data: groupBudgetSourceSetting } = useCtxCompanyGroupBudgetSource()
  const { budgetingScenario } = useSelector(filtersSelector)
  const { openInvestmentModal, openDivestmentModal, openDeprecationModal } = useModalActions()
  const { getDeprecationPlanForAccount } = useDeprecationPlanContext()

  // Lookup deprecation plan from context
  const deprecationPlan = useMemo(() => {
    if (!data) return undefined
    return getDeprecationPlanForAccount({
      balanceSheetRowId: data._sourceStatementRowId
    })
  }, [data, getDeprecationPlanForAccount])

  const isDisabled = !notLocked(budgetingScenario) || groupBudgetSourceSetting === GroupSettingDataSource.subsidiaries

  const handleInvestmentClick = () => {
    if (data) {
      openInvestmentModal({ data })
    }
  }

  const handleDivestmentClick = () => {
    if (data) {
      openDivestmentModal({ data })
    }
  }

  const handleDeprecationClick = () => {
    if (data) {
      openDeprecationModal({ data })
    }
  }

  // Get deprecation button label
  const getDeprecationLabel = () => {
    if (!deprecationPlan) {
      return t('financialStatementsPage:deprecation')
    }
    // deprecationMethodId 1 = straight line depreciation (shows years)
    // deprecationMethodId 2 = reducing balance method (shows percentage)
    if (deprecationPlan.deprecationMethodId === 1) {
      return `${deprecationPlan.value.toFixed(0)}v.`
    }
    return `${(deprecationPlan.value * 100).toFixed(0)}%`
  }

  return (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
      <Button type="default" size="small" disabled={isDisabled} onClick={handleInvestmentClick}>
        {t('financialStatementsPage:investment')}
      </Button>

      <Button type="default" size="small" disabled={isDisabled} onClick={handleDivestmentClick}>
        {t('financialStatementsPage:divestment')}
      </Button>

      <Authorized
        authority={{
          product: ContractProduct.BASIC,
          custom: () => !!data?.depreciable
        }}
      >
        <Button type="default" size="small" disabled={isDisabled} onClick={handleDeprecationClick}>
          {getDeprecationLabel()}
        </Button>
      </Authorized>
    </div>
  )
}
